'use client';
import { useRef } from 'react';
import NextImage from 'next/image';
import { motion, useInView } from 'motion/react';
import { cn } from 'lib/utils';
import { type ImageField, type LinkField } from 'lib/types';
import {
  type BloomProps,
  Bloom,
  type ButtonProps,
  Button,
  Icon,
  Link,
  RichText,
  Wrapper,
} from 'ui';

export type HeaderSectionImageProps = {
  className?: string;
  eyebrow?: string;
  eyebrowAsTitle?: boolean;
  title?: string;
  content?: string;
  image: ImageField;
  alignment?: 'left' | 'right';
  textLink?: LinkField;
  buttonLink?: LinkField;
  buttonLinkVariant?: ButtonProps['variant'];
  bloom?: BloomProps;
};

const HeaderSectionImage = ({
  className,
  eyebrow,
  eyebrowAsTitle,
  alignment = 'left',
  title,
  content,
  textLink,
  buttonLink,
  buttonLinkVariant = 'primary',
  image,
  bloom,
}: HeaderSectionImageProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });

  if (!eyebrow && !title && !content) {
    return null;
  }

  const EyebrowTag = eyebrow && eyebrowAsTitle ? 'h2' : 'p';
  const TitleTag = eyebrow && eyebrowAsTitle ? 'p' : 'h2';

  const getMotionProps = (delay: number) => ({
    initial: { opacity: 0, x: -40 },
    animate: isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 },
    transition: {
      duration: 0.6,
      delay,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  });

  return (
    <motion.section {...{ ref }}>
      <Wrapper
        variant="page-full"
        className={cn(
          'relative flex flex-col-reverse gap-12 lg:gap-24 xl:gap-30 lg:items-center',
          {
            'lg:flex-row': alignment === 'left',
            'lg:flex-row-reverse': alignment === 'right',
          },
          className
        )}
      >
        <Bloom {...bloom} />

        {/* Content */}
        <div className="lg:grow lg:shrink lg:max-w-152 flex flex-col gap-6 relative z-2 text-center lg:text-left">
          <header className="flex flex-col gap-4 lg:gap-6 mx-auto max-w-[60ch] lg:max-w-none">
            {!!eyebrow && (
              <motion.div {...getMotionProps(0.1)}>
                <EyebrowTag
                  className={cn('text-eyebrow font-mono uppercase', {
                    'text-neutral-200': !!title,
                  })}
                >
                  {eyebrow}
                </EyebrowTag>
              </motion.div>
            )}
            {!!title && (
              <motion.div {...getMotionProps(0.2)}>
                <TitleTag className="text-h3">{title}</TitleTag>
              </motion.div>
            )}
            {!!content && (
              <motion.div {...getMotionProps(0.3)}>
                <RichText theme="neutral" {...{ content }} />
              </motion.div>
            )}
          </header>

          {!!textLink && (
            <motion.div {...getMotionProps(0.4)}>
              <Link
                href={textLink.href}
                target={textLink.target}
                variant="neutral"
              >
                <span>{textLink.text}</span>
                <Icon
                  name="carousel-arrow-right"
                  className="inline-block align-middle ml-1.5 w-4 h-auto"
                />
              </Link>
            </motion.div>
          )}

          {!!buttonLink && (
            <motion.div {...getMotionProps(0.5)}>
              <Button
                href={buttonLink.href}
                target={buttonLink.target}
                variant={buttonLinkVariant}
              >
                {buttonLink.text}
              </Button>
            </motion.div>
          )}
        </div>

        {/* Image */}
        <div className="lg:grow lg:shrink xl:shrink-0 relative z-2 flex items-center justify-center">
          <NextImage
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            className="max-w-full rounded-lg"
          />
        </div>
      </Wrapper>
    </motion.section>
  );
};

export default HeaderSectionImage;
